import React, { useState } from 'react';
import { Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { contactInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { toast } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Submit contact message to backend
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data.success) {
        // Show full-page confirmation
        setShowConfirmation(true);
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Failed to Send",
        description: "There was an error sending your message. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Show confirmation page if message was sent successfully
  if (showConfirmation) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-2xl p-12">
            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Message Sent!
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Thank you for contacting LiveryRUs. We've received your message and will get back to you as soon as possible.
            </p>
            
            <Button
              onClick={() => setShowConfirmation(false)}
              className="bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold px-8 py-3 text-lg"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              
              <div className="space-y-6 mb-12">
                <Card className="border-l-4 border-l-[#00D4FF] shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-[#00D4FF]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-1">Phone</h3>
                        <a 
                          href={`tel:${contactInfo.phone}`}
                          className="text-[#00D4FF] hover:text-[#00B8E6] font-semibold transition-colors cursor-pointer"
                        >
                          {contactInfo.phone}
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Click to call</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-[#00D4FF] shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#00D4FF]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 mb-1">Email</h3>
                        <a 
                          href={`mailto:${contactInfo.email}`}
                          className="text-[#00D4FF] hover:text-[#00B8E6] font-semibold transition-colors cursor-pointer"
                        >
                          {contactInfo.email}
                        </a>
                        <p className="text-sm text-gray-500 mt-1">Click to send email - We'll respond promptly</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

 
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                        Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder=""
                        className="h-12"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder=""
                        className="h-12"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-base font-semibold mb-2 block">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder=""
                        className="min-h-32"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold py-6 text-lg transition-all duration-200 hover:scale-105 shadow-xl hover:shadow-[#00D4FF]/50"
                    >
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};